
import React, {
    useState,
    useEffect,
    useContext
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { functions } from "../../../constants/common-functions";

import {
    COLOR_UTILISATION_OCCUPIED,
    COLOR_UTILISATION_SIGNS_OF_LIFE,
    COLOR_UTILISATION_VACANT
} from "../../../constants/colors";

import AmChartStackedEfficiency from "../../../components/AmChartStackedEfficiency";

import { TRUE } from "../../../constants/general-constants";

import {
    CHART_CONTAINER_STYLE,
    DEFAULT_BLUE_GRADIENT
} from "../../../constants/styles";

import PillButton from "../../../components/PillButton";

import { store } from "../../../context/store";


//========================================================
// Utilisation Chart Efficiency
//========================================================

export default function UtilisationChartEfficiency({
    id,
    isMeeting,
    filterBean,
    parentSpaceTypeId
}) {

    //----------------------------------------------------
    // Defaults
    //----------------------------------------------------

    const defaultStartDate = "2000-01-01";
    const defaultEndDate = "9999-09-01";


    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [chartIndex, setChartIndex] = useState(0);

    const [dataEfficiency, setDataEfficiency] = useState([]);

    const [dataEfficiencyAll, setDataEfficiencyAll] = useState([]);

    const [startDate, setStartDate] = useState(defaultStartDate);

    const [endDate, setEndDate] = useState(defaultEndDate);

    const [efficiencyYMax, setEfficiencyYMax] = useState(0);

    const [
        efficiencyTargetLines,
        setEfficiencyTargetLines
    ] = useState([]);

    const [titles, setTitles] = useState([]);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const [
        showExpandedChart,
        setShowExpandedChart
    ] = useState(false);


    //----------------------------------------------------
    // Global Context
    //----------------------------------------------------

    const globalState = useContext(store);

    const queryContext =
        globalState?.state?.queryContext;


    //----------------------------------------------------
    // Chart Keys
    //----------------------------------------------------

    const dataEfficiencyChartKeys = [

        {
            id: "occupied",
            title: "Occupied",
            color: COLOR_UTILISATION_OCCUPIED
        },

        {
            id: "signsOfLife",
            title: "Signs Of Life",
            color: COLOR_UTILISATION_SIGNS_OF_LIFE
        },

        {
            id: "vacant",
            title: "Available",
            color: COLOR_UTILISATION_VACANT
        }

    ];


    //====================================================
    // Load Efficiency Data
    //====================================================

    const loadDataEfficiencyCurve = (
        currentFilterBean,
        showAll,
        callback
    ) => {

        let url = "";

        if (isMeeting === TRUE) {

            url =
                "/resolutionsoftwareplatform/utilisationstandardgetefficiencycurvemeeting";

        }
        else {

            url =
                "/resolutionsoftwareplatform/utilisationstandardgetefficiencycurve";

        }

        functions.postFilterBeanAjax(

            url
                + "?startDate="
                + startDate
                + "&endDate="
                + endDate
                + "&showAll="
                + showAll,

            functions.buildPostBodyFilterBeanQueryContext(
                currentFilterBean,
                queryContext
            ),

            callback

        );

    };


    //====================================================
    // Process Efficiency Data
    //====================================================

    const handleDataEfficiencyCurve = (
        obj,
        setDataFn
    ) => {

        if (!obj) {

            console.log("OBJ UNDEFINED");

            setDataFn([]);

            setTitles([]);

            return;

        }

        if (!Array.isArray(obj.weekBatches)) {

            console.log("weekBatches missing");

            console.log(obj);

            setDataFn([]);

            setTitles([]);

            return;

        }

        if (!obj.utilisationEfficiencyCurve) {

            console.log("utilisationEfficiencyCurve missing");

            console.log(obj);

            setDataFn([]);

            setTitles([]);

            return;

        }


        //------------------------------------------------
        // Period Titles
        //------------------------------------------------

        const tempTitles = [];

        tempTitles.push({

            startDate: defaultStartDate,

            endDate: defaultEndDate,

            title: "All"

        });

        for (
            let i = 0;
            i < obj.weekBatches.length;
            i++
        ) {

            tempTitles.push({

                startDate:
                    obj.weekBatches[i].startDate,

                endDate:
                    obj.weekBatches[i].endDate,

                title:
                    obj.weekBatches[i].title

            });

        }

        setTitles(tempTitles);


        //------------------------------------------------
        // Efficiency Curve
        //------------------------------------------------

        const utilisationEfficiencyCurve =
            obj.utilisationEfficiencyCurve;

        const tempTargetLines = [];


        //------------------------------------------------
        // Target Lines
        //------------------------------------------------

        if (utilisationEfficiencyCurve.number20) {

            tempTargetLines.push({

                value: 20,

                title:
                    "20%: "
                    + functions.formatNumber(
                        utilisationEfficiencyCurve.number20
                    )

            });

        }

        if (utilisationEfficiencyCurve.number40) {

            tempTargetLines.push({

                value: 40,

                title:
                    "40%: "
                    + functions.formatNumber(
                        utilisationEfficiencyCurve.number40
                        - utilisationEfficiencyCurve.number20
                    )

            });

        }

        if (utilisationEfficiencyCurve.number60) {

            tempTargetLines.push({

                value: 60,

                title:
                    "60%: "
                    + functions.formatNumber(
                        utilisationEfficiencyCurve.number60
                        - utilisationEfficiencyCurve.number40
                    )

            });

        }

        if (utilisationEfficiencyCurve.number80) {

            tempTargetLines.push({

                value: 80,

                title:
                    "80%: "
                    + functions.formatNumber(
                        utilisationEfficiencyCurve.number80
                        - utilisationEfficiencyCurve.number60
                    )

            });

        }


        //------------------------------------------------
        // Chart Data
        //------------------------------------------------

        if (
            Array.isArray(
                utilisationEfficiencyCurve.efficiencyCurveEntries
            )
        ) {

            setDataFn(
                utilisationEfficiencyCurve
                    .efficiencyCurveEntries
                    .slice()
            );

        }
        else {

            setDataFn([]);

        }


        //------------------------------------------------
        // Chart Limits
        //------------------------------------------------

        setEfficiencyYMax(
            utilisationEfficiencyCurve.ymax
        );

        setEfficiencyTargetLines(
            tempTargetLines
        );

    };


    //====================================================
    // Load Main Chart
    //====================================================

    useEffect(() => {

        if (parentSpaceTypeId === "-100") {

            return;

        }

        loadDataEfficiencyCurve(

            filterBean,

            false,

            function (obj) {

                handleDataEfficiencyCurve(
                    obj,
                    setDataEfficiency
                );

            }

        );

    }, [

        id,
        filterBean,
        chartIndex,
        startDate,
        endDate,
        parentSpaceTypeId,
        queryContext?.utilisationSurveyId

    ]);


    //====================================================
    // Load Expanded Chart Data
    //====================================================

    useEffect(() => {

        if (!isDrawerOpen) {

            return;

        }

        if (dataEfficiencyAll.length > 0) {

            return;

        }

        loadDataEfficiencyCurve(

            filterBean,

            true,

            function (obj) {

                handleDataEfficiencyCurve(
                    obj,
                    setDataEfficiencyAll
                );

            }

        );

    }, [

        isDrawerOpen,
        filterBean,
        startDate,
        endDate,
        dataEfficiencyAll.length,
        queryContext?.utilisationSurveyId

    ]);


    //====================================================
    // Reset Expanded Data When Filters Change
    //====================================================

    useEffect(() => {

        setDataEfficiencyAll([]);

        setShowExpandedChart(false);

    }, [

        filterBean,
        queryContext?.utilisationSurveyId

    ]);


    //====================================================
    // Expanded Chart Initialisation
    //
    // The drawer is mounted immediately when opened.
    // Allow the browser to calculate its dimensions
    // before mounting the amCharts component.
    //====================================================

    useEffect(() => {

        if (!isDrawerOpen) {

            setShowExpandedChart(false);

            return;

        }

        setShowExpandedChart(false);

        const timer = setTimeout(function () {

            setShowExpandedChart(true);

        }, 150);

        return function () {

            clearTimeout(timer);

        };

    }, [isDrawerOpen]);


    //====================================================
    // Active Dataset
    //====================================================

    const getActiveDataset = function () {

        if (
            Array.isArray(dataEfficiencyAll)
            && dataEfficiencyAll.length > 0
        ) {

            return dataEfficiencyAll;

        }

        return dataEfficiency;

    };


    //====================================================
    // Total Worksettings
    //====================================================

    const getTotalDesks = function () {

        const data = getActiveDataset();

        if (!Array.isArray(data)) {

            return 0;

        }

        return data.length;

    };


    //====================================================
    // Fully Vacant Worksettings
    //====================================================

    const getFullyVacantCount = function () {

        const data = getActiveDataset();

        if (!Array.isArray(data)) {

            return 0;

        }

        let count = 0;

        for (
            let i = 0;
            i < data.length;
            i++
        ) {

            const row = data[i];

            const occupied = row.occupied || 0;

            const sol = row.signsOfLife || 0;

            if (
                occupied === 0
                && sol === 0
            ) {

                count++;

            }

        }

        return count;

    };


    //====================================================
    // Period Selection
    //====================================================

    const switchChartIndex = (item, index) => {

        setChartIndex(index);

        setStartDate(item.startDate);

        setEndDate(item.endDate);

        setDataEfficiencyAll([]);

    };


    const titleDisplay = titles.map(
        (item, index) => (

            <PillButton

                key={index}

                isActive={index === chartIndex}

                id={index}

                label={item.title}

                onClick={() => {

                    switchChartIndex(
                        item,
                        index
                    );

                }}

            />

        )
    );


    //====================================================
    // Styles
    //====================================================

    const thisContainer = {

        display: "flex",

        width: "100%",

        flexDirection: "column",

        height: "100%",

        minWidth: 0

    };


    const chartContainer = {

        display: "flex",

        width: "calc(100% - 10px)",

        marginLeft: "10px",

        height: "calc(100% - 40px)",

        position: "relative",

        minWidth: 0

    };


    //----------------------------------------------------
    // Expanded Drawer
    //----------------------------------------------------

    const drawer = {

        position: "fixed",

        top: "94px",

        right: 0,

        width: "calc(100vw - 380px)",

        height: "calc(100vh - 94px)",

        background: "#ffffff",

        borderLeft: "1px solid #e3e6ea",

        boxShadow:
            "-12px 0 30px rgba(0,0,0,0.18)",

        zIndex: 1000,

        display: "flex",

        flexDirection: "column",

        overflow: "hidden",

        boxSizing: "border-box"

    };


    const drawerContent = {

        padding: "20px",

        height: "100%",

        minHeight: 0,

        boxSizing: "border-box",

        display: "flex",

        flexDirection: "column",

        overflow: "hidden"

    };


    //----------------------------------------------------
    // Main Chart Title
    //----------------------------------------------------

    const titleContainer = {

        display: "flex",

        fontSize: "12px",

        height: "20px",

        alignItems: "center",

        alignSelf: "center",

        flexShrink: 0

    };


    const chartTitle = {

        display: "flex",

        width: "100%",

        height: "20px",

        fontSize: "12px",

        fontWeight: "bold",

        alignItems: "center",

        justifyContent: "space-around",

        marginTop: "-10px"

    };


    //----------------------------------------------------
    // Expand Button
    //----------------------------------------------------

    const expandIcon = {

        position: "absolute",

        bottom: "8px",

        right: "10px",

        width: "22px",

        height: "22px",

        borderRadius: "4px",

        background: "#0c3558",

        border:
            "1px solid rgba(141,200,240,0.22)",

        color: "#ffffff",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        fontSize: "11px",

        cursor: "pointer",

        boxShadow:
            "0 3px 8px rgba(7,28,49,0.20)",

        transition: "all 0.2s ease",

        zIndex: 2

    };


    //----------------------------------------------------
    // Close Button
    //----------------------------------------------------

    const closeButton = {

        position: "absolute",

        top: "12px",

        right: "14px",

        width: "30px",

        height: "30px",

        borderRadius: "50%",

        background: "#0c3558",

        border:
            "1px solid rgba(141,200,240,0.22)",

        color: "#ffffff",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        cursor: "pointer",

        boxShadow:
            "0 4px 10px rgba(7,28,49,0.18)",

        transition: "all 0.2s ease",

        zIndex: 5

    };


    //----------------------------------------------------
    // Expanded Chart Area
    //----------------------------------------------------

    const expandedChartContainer = {

        width: "100%",

        flex: "1 1 auto",

        minHeight: 0,

        position: "relative",

        overflow: "hidden"

    };


    //----------------------------------------------------
    // Statistics
    //----------------------------------------------------

    const statsContainer = {

        display: "flex",

        justifyContent: "flex-start",

        marginTop: "12px",

        gap: "12px",

        flexShrink: 0,

        flexWrap: "wrap"

    };


    const statBox = {

        background: "#0c3558",

        color: "#ffffff",

        padding: "8px 14px",

        borderRadius: "4px",

        border:
            "1px solid rgba(141,200,240,0.18)",

        fontSize: "12px",

        fontWeight: "500",

        display: "flex",

        alignItems: "center",

        gap: "8px",

        boxShadow:
            "0 5px 14px rgba(7,28,49,0.12)"

    };


    const blueDot = {

        width: "8px",

        height: "8px",

        borderRadius: "50%",

        background: "#8dc8f0",

        flexShrink: 0

    };


    const greenDot = {

        width: "8px",

        height: "8px",

        borderRadius: "50%",

        background: "#22c55e",

        flexShrink: 0

    };


    //====================================================
    // Render
    //====================================================

    return (

        <>

            {/*============================================*/}
            {/* Main Efficiency Chart                       */}
            {/*============================================*/}

            <div style={thisContainer}>

                <div style={titleContainer}>

                    {titleDisplay}

                </div>


                <div style={chartContainer}>

                    <div style={CHART_CONTAINER_STYLE}>

                        <AmChartStackedEfficiency

                            key={id}

                            chartId={id}

                            width="100%"

                            height="100%"

                            chartData={dataEfficiency}

                            chartKeys={dataEfficiencyChartKeys}

                            categoryKey="code"

                            isStacked="true"

                            hideBullets={TRUE}

                            targetLines={efficiencyTargetLines}

                            hideLabels={TRUE}

                            yMax={efficiencyYMax}

                            isExpanded={false}

                            disableHover={TRUE}

                        />


                        <div

                            style={expandIcon}

                            onClick={() => {

                                setIsDrawerOpen(true);

                            }}

                            title="Expand Worksetting Efficiency"

                        >

                            <FontAwesomeIcon icon="expand" />

                        </div>

                    </div>

                </div>


                <div style={chartTitle}>

                    Worksetting Efficiency

                </div>

            </div>


            {/*============================================*/}
            {/* Expanded Efficiency Drawer                 */}
            {/*============================================*/}

            {isDrawerOpen && (

                <div style={drawer}>

                    {/*------------------------------------*/}
                    {/* Close Drawer                       */}
                    {/*------------------------------------*/}

                    <div

                        style={closeButton}

                        onClick={() => {

                            setIsDrawerOpen(false);

                        }}

                        title="Close expanded chart"

                    >

                        <FontAwesomeIcon icon="times" />

                    </div>


                    {/*------------------------------------*/}
                    {/* Drawer Content                     */}
                    {/*------------------------------------*/}

                    <div style={drawerContent}>

                        <h3
                            style={{
                                margin: "4px 48px 12px 0",
                                flexShrink: 0
                            }}
                        >

                            Worksetting Efficiency (Expanded)

                        </h3>


                        {/*--------------------------------*/}
                        {/* Period Selector                */}
                        {/*--------------------------------*/}

                        <div style={titleContainer}>

                            {titleDisplay}

                        </div>


                        <div
                            style={{
                                height: "10px",
                                flexShrink: 0
                            }}
                        />


                        {/*--------------------------------*/}
                        {/* Expanded Chart                 */}
                        {/*--------------------------------*/}

                        <div style={expandedChartContainer}>

                            {showExpandedChart ? (

                                <AmChartStackedEfficiency

                                    key={id + "-expanded"}

                                    chartId={id + "-expanded"}

                                    width="100%"

                                    height="100%"

                                    chartData={getActiveDataset()}

                                    chartKeys={dataEfficiencyChartKeys}

                                    categoryKey="code"

                                    isStacked="true"

                                    hideBullets={false}

                                    targetLines={efficiencyTargetLines}

                                    hideLabels={TRUE}

                                    yMax={efficiencyYMax}

                                    isExpanded={true}

                                />

                            ) : (

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                        height: "100%"
                                    }}
                                >

                                    Loading expanded chart...

                                </div>

                            )}

                        </div>


                        {/*--------------------------------*/}
                        {/* Worksettings Label             */}
                        {/*--------------------------------*/}

                        <div
                            style={{
                                display: "flex",
                                marginTop: "10px",
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                color: "#333",
                                flexShrink: 0
                            }}
                        >

                            Work Settings

                        </div>


                        {/*--------------------------------*/}
                        {/* Statistics                     */}
                        {/*--------------------------------*/}

                        <div style={statsContainer}>

                            <div style={statBox}>

                                <div style={blueDot} />

                                Total Worksettings: {getTotalDesks()}

                            </div>


                            <div style={statBox}>

                                <div style={greenDot} />

                                Vacant For Duration: {getFullyVacantCount()}

                            </div>

                        </div>

                    </div>

                </div>

            )}

        </>

    );

}
